import React from "react";
import { ToastContainer } from "react-toastify";

import { ConfirmationInitializationMessage } from "@components/Common/ConfirmationInitializationMessage";
import { ConfirmationUpdatingDataMessageModal } from "@components/Common/ConfirmationUpdatingDataMessageModal";
import HistoryModal from "./HistoryModal";
import { ConfirmationModalArchived } from "../ConfirmationModalArchived";
import { ConfirmationModalActivedApplication } from "../ConfirmationModalActivedApplication";
import { DeviceAssociateModal } from "./DeviceAssociateModal/index";
import DeviceDissociateModal from "./DeviceDissociateModal";
import { ConfirmationDissociate } from "./ConfirmationDissociate";
import PrinterSetting from "./PrinterSetting";
import OriginHistory from "@pages/Peripherals/Printers/OriginHistory";
import CashManagementSetting from "./CashManagementSetting";
import { MoneticValinaSetting } from "./MoneticSetting/MoneticValinaSetting";
import { MoneticsHistory } from "@pages/Peripherals/Monetics/MoneticsHistory";
import CashManagementHistoy from "@pages/Peripherals/CashManagement/HistoryModal";
import { setIsEdited } from "../store";
import { APP_ID } from "./constants";

import { ModalStates } from "./types";

type ModalsContainerProps = {
    modals: ModalStates;
    toggleModal: (key: keyof ModalStates, value: boolean) => void;
    selectedRows: any;
    setSelectedRows: (rows: any) => void;
    isDataUpdated: boolean | number;
    setIsDataUpdated: (value: boolean | number) => void;
    isAddNewApplicationButtonClicked: boolean;
    setIsAddNewApplicationButtonClicked: (value: boolean) => void;
    isConfigurationPrinterClicked: boolean;
    isConfigurationCashManagementClicked: boolean;
    isConfigurationMoneticClicked: boolean;
    isActionsClicked: boolean;
    setIsActionsClicked: (value: boolean) => void;
    validationAction: (() => void) | Record<string, any>;
    actions: () => void;
    urlHistoryPrinter: string;
    oneShop: boolean;
    userID: string;
    franchiseID: string;
    shopID: string;
};

const ModalsContainer = ({
    modals,
    toggleModal,
    selectedRows,
    setSelectedRows,
    isDataUpdated,
    setIsDataUpdated,
    isAddNewApplicationButtonClicked,
    setIsAddNewApplicationButtonClicked,
    isConfigurationPrinterClicked,
    isConfigurationCashManagementClicked,
    isConfigurationMoneticClicked,
    isActionsClicked,
    setIsActionsClicked,
    validationAction,
    actions,
    urlHistoryPrinter,
    oneShop,
    userID,
    franchiseID,
    shopID,
}: ModalsContainerProps) => {
    const handleSetIsDataUpdated = (value: boolean | number) => {
        setIsDataUpdated(value);
    };

    return (
        <>
            {modals.initialization && (
                <ConfirmationInitializationMessage
                    isModalOpened={modals.initialization}
                    setIsModalOpened={(v: boolean) =>
                        toggleModal("initialization", v)
                    }
                    setIsClicked={setIsAddNewApplicationButtonClicked}
                    isClicked={isAddNewApplicationButtonClicked}
                    setIsEdited={setIsEdited}
                />
            )}

            {modals.history && (
                <HistoryModal
                    selectedRows={selectedRows.map((row: any) => ({
                        shopId: row.shopId,
                        appId: row.appId,
                    }))}
                    setIsModalOpened={(v: boolean) =>
                        toggleModal("history", v)
                    }
                    isModalOpened={modals.history}
                    setIsDataUpdated={handleSetIsDataUpdated}
                    setIsAddApplication={(v: boolean) =>
                        toggleModal("addApplication", v)
                    }
                />
            )}

            {modals.confirmArchived && (
                <ConfirmationModalArchived
                    setIsConfirmationModalArchived={(v: boolean) =>
                        toggleModal("confirmArchived", v)
                    }
                    isConfirmationModalArchived={modals.confirmArchived}
                    setIsDataUpdated={handleSetIsDataUpdated}
                    selectedRows={selectedRows}
                />
            )}

            {modals.confirmActived && (
                <ConfirmationModalActivedApplication
                    setIsConfirmationModalActived={(v: boolean) =>
                        toggleModal("confirmActived", v)
                    }
                    isConfirmationModalActived={modals.confirmActived}
                    setIsDataUpdated={handleSetIsDataUpdated}
                    selectedRows={selectedRows}
                />
            )}

            {modals.deviceAssociate && (
                <DeviceAssociateModal
                    setIsDeviceAssociateModalOpened={(v: boolean) =>
                        toggleModal("deviceAssociate", v)
                    }
                    isDeviceAssociateModalOpened={modals.deviceAssociate}
                    selectedRows={selectedRows}
                    isDataUpdated={isDataUpdated}
                    setDataIsUpdated={handleSetIsDataUpdated}
                    oneShop={oneShop}
                />
            )}

            <DeviceDissociateModal
                selectedRows={selectedRows}
                setDataIsUpdated={handleSetIsDataUpdated}
                setIsDeviceDissociateModalOpened={(v: boolean) =>
                    toggleModal("deviceDissociate", v)
                }
                isDeviceDissociateModalOpened={modals.deviceDissociate}
            />

            {modals.multiDissociate && (
                <ConfirmationDissociate
                    isModalOpened={modals.multiDissociate}
                    setIsModalOpened={(v: boolean) =>
                        toggleModal("multiDissociate", v)
                    }
                    selectedRows={selectedRows}
                    setDataIsUpdated={handleSetIsDataUpdated}
                />
            )}

            {isConfigurationPrinterClicked && (
                <PrinterSetting
                    rowData={selectedRows}
                    isDataUpdated={isDataUpdated}
                    setDataIsUpdated={handleSetIsDataUpdated}
                    oneShop={oneShop}
                />
            )}

            {isConfigurationCashManagementClicked && (
                <CashManagementSetting
                    rowData={selectedRows}
                    setRowData={setSelectedRows}
                    setDataIsUpdated={handleSetIsDataUpdated}
                    oneShop={oneShop}
                />
            )}

            {isConfigurationMoneticClicked && (
                <MoneticValinaSetting
                    infoData={{
                        userId: userID,
                        shopId: selectedRows.shopId,
                        franchiseId: franchiseID,
                        appId: APP_ID,
                        appId_children: selectedRows.appChildrenId,
                    }}
                    data={selectedRows}
                    setDataIsUpdated={handleSetIsDataUpdated}
                />
            )}

            {modals.printerOriginHistory && (
                <OriginHistory
                    url={urlHistoryPrinter}
                    setIsModalOpened={(v: boolean) =>
                        toggleModal("printerOriginHistory", v)
                    }
                    isModalOpened={modals.printerOriginHistory}
                />
            )}

            {modals.moneticHistory && (
                <MoneticsHistory
                    selectedRows={selectedRows}
                    isHistoryModalOpened={modals.moneticHistory}
                    setIsHistoryModalOpened={(v: boolean) =>
                        toggleModal("moneticHistory", v)
                    }
                    setGlobalDataUpdated={setIsDataUpdated}
                />
            )}

            {modals.cashManagementHistory && (
                <CashManagementHistoy
                    isModalOpened={modals.cashManagementHistory}
                    setIsModalOpened={(v: boolean) =>
                        toggleModal("cashManagementHistory", v)
                    }
                    setGlobalDataUpdated={setIsDataUpdated}
                    selectedRows={selectedRows.map((row: any) => ({
                        shopId: row.shopId,
                        peripheralId: row.peripheralId,
                        peripheriqalName: row.Périphérique,
                    }))}
                />
            )}

            {isActionsClicked && (
                <ConfirmationUpdatingDataMessageModal
                    isModalOpened={isActionsClicked}
                    setIsModalOpened={setIsActionsClicked}
                    validationAction={validationAction}
                    actions={actions}
                    setIsEdited={setIsEdited}
                />
            )}

            <ToastContainer limit={1} />
        </>
    );
};

export default ModalsContainer;
